import { getTilesStore } from 'stores/RootStoreProvider'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { type BaseTileStore } from 'stores/tilesStore/tiles/BaseTileStore'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import { type Crop } from 'utils/cropUtils.types'
import {
  generateCollageSvg,
  getCollageDpi,
  getPhotoCroppingParams,
} from './collageService'
import { fetchLayoutSVGText } from './layoutFetcher'
import { transformImageUrl } from '../CloudinaryService'
import { calculateThumbSizeForRatio } from '../frameThumbsCreator'
import { MAX_COLLAGE_IMAGE_SIZE } from 'components/CollageTile/CollageSvg/CollageSvg.consts'
import { tileSizeToImageDimensions } from 'utils/ImageUtils'
import { uploadPhotoWithRetries } from '../photoUploader'
import { logger } from '../logger'

export interface CollageImageUpload {
  url: string
  uploadedAt: number
  cropParams: Record<string, Crop>
}

// Composites a collage tile into an SVG and uploads it, returning the print
// image. Provider-free (reads the root tiles store) so any checkout entry
// point — including the client-only cart island — can produce the collage
// image, not just the PhotoStyler designer.
export async function generateCollageImageUpload(
  collageTile: Tile
): Promise<CollageImageUpload> {
  const tilePhotosUrls = await readOutsideReaction(() => {
    const storeTiles = [getTilesStore().tryGetTile(collageTile.id)].filter(
      Boolean
    ) as BaseTileStore[]
    return getTilesStore().getTilePhotosUrls(storeTiles)
  })

  const { layout, photos, collageText = {} } = collageTile.collage ?? {}

  const photoUrls = Object.fromEntries(
    Object.entries(photos ?? {}).map(([photoId, photo]) => {
      const { url } = tilePhotosUrls[Number(photoId)]
      if (!url) {
        throw new Error(`Photo ${photoId} not found in tile photos urls`)
      }

      const { cropParams } = getPhotoCroppingParams(photo)
      const { width, height } = cropParams
      return [
        photoId,
        transformImageUrl(
          url,
          cropParams,
          calculateThumbSizeForRatio(width, height, MAX_COLLAGE_IMAGE_SIZE),
          true
        ),
      ]
    })
  )

  const svgLayout = await fetchLayoutSVGText(layout)
  const outputImageDimensions = tileSizeToImageDimensions({
    tileSize: collageTile.tileSize,
    materialType: collageTile.materialType,
    dpi: getCollageDpi(collageTile.tileSize),
  })
  const svg = generateCollageSvg({
    svgLayout,
    photoUrls,
    outputImageDimensions,
    collageText,
  })

  const url = await uploadPhotoWithRetries({
    file: svg as unknown as File,
    contentType: 'image/svg+xml',
  })

  logger.info('Collage composite uploaded', { tileId: collageTile.id, url })

  return {
    url,
    uploadedAt: Date.now(),
    cropParams: {
      [collageTile.currentAspectRatio]: {
        x: 0,
        y: 0,
        cropType: 'default',
        ...outputImageDimensions,
      },
    },
  }
}
